import React, { useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { get, isEmpty } from 'lodash';

import { SagisConfig } from 'containers/AppContainer/typings';
import { translate } from 'containers/services/translate';
import messages from 'containers/RecapManagerPage/messages';
import {
  VALIDATION_DECISION,
  EMPTY_VALIDATION_DECISION,
} from 'containers/data-structure';
import validate, { formsAreValidated } from 'containers/services/validateForm';
import {
  MANAGEMENT_MODES,
  SCALE_CASES_KEY,
} from 'containers/RecapManagerPage/constants';

export const commonRightValidationConstraints = (
  decision: any,
  isChangeNatureEnabled: boolean | undefined,
  isConfirmationPreopeningEnabled: boolean | undefined,
) => {
  return {
    [VALIDATION_DECISION.CIRCUMSTANCE]: {
      presence:
        get(decision, [VALIDATION_DECISION.SCALE_CASE], '') ===
          SCALE_CASES_KEY.OTHER &&
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
          MANAGEMENT_MODES.COMMON_RIGHT,
    },
    [VALIDATION_DECISION.RESPONSIBILITY]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.COMMON_RIGHT,
    },
    [VALIDATION_DECISION.CIRCUMSTANCE_CODE]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.COMMON_RIGHT,
    },
    [VALIDATION_DECISION.SCALE_CASE]: {
      presence:
        decision[VALIDATION_DECISION.MANAGEMENT_MODE] ===
        MANAGEMENT_MODES.COMMON_RIGHT,
    },
    [VALIDATION_DECISION.MANAGEMENT_MODE]: {
      presence: true,
    },
    [VALIDATION_DECISION.CHANGE_NATURE]: {
      presence: isChangeNatureEnabled,
    },
    [VALIDATION_DECISION.CONFIRMATION_PREOPENING]: {
      presence: isConfirmationPreopeningEnabled,
    },
  };
};

export const commonRightValidationsValidator = (
  decision: any,
  isChangeNatureEnabled: boolean,
  isConfirmationPreopeningEnabled: boolean,
) => {
  return validate(
    decision,
    commonRightValidationConstraints(
      decision,
      isChangeNatureEnabled,
      isConfirmationPreopeningEnabled,
    ),
  );
};

export interface CommonRightValidationFormProps {
  intl: IntlShape;
  responsabilities: Array<SagisConfig>;
  decision: any;
  updateDecision: Function;
  updateDecisionValidation: Function;
  isChangeNatureEnabled: boolean;
  isConfirmationPreopeningEnabled: boolean;
}
const Container = styled.div`
  &&.root {
    display: flex;
    flex-direction: column;
    margin-top: 0px;
  }
  .input {
    width: 327px;
    margin-bottom: 33px;
    margin-left: 24px;
    margin-right: 24px;
  }
  .label {
    font-size: 13px;
    font-family: Roboto;
  }
  .MuiFormLabel-root {
    font-size: 13px;
    color: #4f4f4f;
  }
  .MuiInputLabel-shrink {
    color: #00000061;
  }
  .MuiFormLabel-root.Mui-focused {
    color: rgba(9, 122, 194, 1);
  }
`;

const CommonRightValidationForm: React.FC<CommonRightValidationFormProps> = (
  props,
): JSX.Element => {
  useEffect(() => {
    const expectedDecision = {
      ...EMPTY_VALIDATION_DECISION,
      [VALIDATION_DECISION.MANAGEMENT_MODE]:
        props.decision[VALIDATION_DECISION.MANAGEMENT_MODE],
    };
    props.updateDecision(expectedDecision);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.decision[VALIDATION_DECISION.MANAGEMENT_MODE]]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<{ name?: string; value: any }>,
  ) => {
    const { value } = event.target;
    let expectedDecision: any = { ...props.decision };
    expectedDecision[field] = value;
    props.updateDecision(expectedDecision);
    const expectedDecisionValidation =
      commonRightValidationsValidator(
        expectedDecision,
        props.isChangeNatureEnabled,
        props.isConfirmationPreopeningEnabled,
      ) || {};
    props.updateDecisionValidation(
      formsAreValidated(expectedDecisionValidation),
    );
  };

  const inputLabel = React.useRef<HTMLLabelElement>(null);
  return (
    <Container>
      <div className="fields">
        <TextField
          onChange={handleChange(VALIDATION_DECISION.CIRCUMSTANCE)}
          name={VALIDATION_DECISION.CIRCUMSTANCE}
          value={get(props.decision, [VALIDATION_DECISION.CIRCUMSTANCE])}
          label={translate({ ...messages.circumstance }, props)}
          className="input"
          multiline
        />
        <FormControl className="input">
          <InputLabel ref={inputLabel} id="responsibility" className="label">
            {translate({ ...messages.responsibility }, props)}
          </InputLabel>
          <Select
            name={VALIDATION_DECISION.RESPONSIBILITY}
            value={get(props.decision, [VALIDATION_DECISION.RESPONSIBILITY])}
            onChange={handleChange(VALIDATION_DECISION.RESPONSIBILITY)}
          >
            {!isEmpty(props.responsabilities) &&
              props.responsabilities.map(responsability => (
                <MenuItem
                  value={responsability.ItemKey}
                  key={responsability.ItemKey}
                >
                  {responsability.ItemDescription}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <TextField
          onChange={handleChange(VALIDATION_DECISION.COMMENT)}
          name={VALIDATION_DECISION.COMMENT}
          value={get(props.decision, [VALIDATION_DECISION.COMMENT])}
          label={translate({ ...messages.comment }, props)}
          className="input"
          multiline
        />
      </div>
    </Container>
  );
};

export default injectIntl(CommonRightValidationForm);
